import React, { useContext, useEffect, useMemo, useState } from 'react';
import { fetchWithAuth } from '../utils/authUtils';
import { useParams, useNavigate } from 'react-router-dom';
import { DataContext } from '../Provider/dataProvider';
import {
	canConfirmMedicationTaken,
	isMedicationDue,
	medicationTooEarlyMessage,
} from '../utils/medicationLogUtils';
import { isMembershipAccessDenied } from '../utils/membershipAccess';
import MembershipRequiredView from '../components/MembershipRequiredView';
import { FEATURES } from '../constants/featureAccess';
import { useElderlyFeatureAccess } from '../hooks/useFeatureAccess';
import '../css/medicationChecklist.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const formatTime = (value) => {
	if (!value) return 'N/A';
	return value.substring(0, 5);
};

const formatDateTime = (value) => {
	if (!value) return '';
	const date = new Date(value);
	return date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
};

const MedicationChecklist = () => {
	const { elderlyId: paramElderlyId } = useParams();
	const navigate = useNavigate();
	const { data } = useContext(DataContext);
	const [checklist, setChecklist] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
	const [expandedTask, setExpandedTask] = useState(null);
	const [sideEffectNote, setSideEffectNote] = useState({});
	const [actionError, setActionError] = useState('');
	const [membershipDenied, setMembershipDenied] = useState(false);

	const resolvedElderlyId = useMemo(() => paramElderlyId || data?.profileId || data?.id || '', [paramElderlyId, data]);
	const { canUse: canUseForElderly, loading: accessLoading } = useElderlyFeatureAccess(resolvedElderlyId);
	const currentRole = String(data?.role || '').toLowerCase();
	const isRelative = currentRole === 'relative';

	useEffect(() => {
		if (!resolvedElderlyId) return;

		const fetchChecklist = async () => {
			try {
				setLoading(true);
				setError('');
				setMembershipDenied(false);
				const response = await fetchWithAuth(`${API_BASE}/api/medication-logs/checklist/${resolvedElderlyId}?date=${selectedDate}`);

				if (!response.ok) {
					const errorBody = await response.json().catch(() => ({}));
					if (isMembershipAccessDenied(response.status, errorBody)) {
						setMembershipDenied(true);
						setChecklist([]);
						return;
					}
					throw new Error(errorBody.message || 'Không thể tải lịch nhắc uống thuốc.');
				}

				const payload = await response.json();
				setChecklist(Array.isArray(payload?.data) ? payload.data : []);
			} catch (err) {
				setError(err.message);
				setChecklist([]);
			} finally {
				setLoading(false);
			}
		};

		fetchChecklist();
	}, [resolvedElderlyId, selectedDate]);

	const handleConfirmTaken = async (task) => {
		setActionError('');
		if (!canConfirmMedicationTaken(task)) return;
		if (!isMedicationDue(task)) {
			setActionError(medicationTooEarlyMessage(task));
			return;
		}

		try {
			const response = await fetchWithAuth(`${API_BASE}/api/medication-logs/${task.logId}`, {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					status: 'taken',
					sideEffect: sideEffectNote[task.logId] || null,
				}),
			});

			const errorBody = await response.json().catch(() => ({}));
			if (!response.ok) {
				throw new Error(errorBody.message || 'Lỗi khi cập nhật trạng thái.');
			}

			setChecklist((prev) =>
				prev.map((item) =>
					item.logId === task.logId
						? { ...item, status: 'taken', takenAt: new Date().toISOString() }
						: item
				)
			);
			setExpandedTask(null);
		} catch (err) {
			setActionError(err.message);
		}
	};

	const groupByTime = (tasks) => {
		const timeMap = {};
		tasks.forEach((task) => {
			const time = task.remindTime || '00:00';
			if (!timeMap[time]) {
				timeMap[time] = [];
			}
			timeMap[time].push(task);
		});
		return Object.entries(timeMap).sort((a, b) => a[0].localeCompare(b[0]));
	};

	const groupedTasks = groupByTime(checklist);
	const todayFormatted = new Date(selectedDate).toLocaleDateString('vi-VN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

	if (loading || (resolvedElderlyId && accessLoading)) {
		return (
			<div className="medication-checklist-page">
				<div className="checklist-container">
					<h2>📋 Lịch nhắc uống thuốc hôm nay</h2>
					<p className="loading-state">Đang tải danh sách thuốc...</p>
				</div>
			</div>
		);
	}

	if (
		membershipDenied
		|| (resolvedElderlyId && data?.role !== 'admin' && !canUseForElderly(FEATURES.MEDICATION))
	) {
		return (
			<div className="medication-checklist-page">
				<div className="checklist-container checklist-container--membership">
					<div className="checklist-membership-body">
						<MembershipRequiredView />
					</div>
				</div>
			</div>
		);
	}

	if (error && checklist.length === 0) {
		return (
			<div className="medication-checklist-page">
				<div className="checklist-container">
					<h2>📋 Lịch nhắc uống thuốc hôm nay</h2>
					<p className="error-state">Lỗi: {error}</p>
				</div>
			</div>
		);
	}

	return (
		<div className="medication-checklist-page">
			<div className="checklist-container">
				<div className="checklist-header">
					<div className="header-left">
						{isRelative && (
							<button className="back-button" onClick={() => navigate('/medication-checklist')}>
								<i className="fa-solid fa-arrow-left"></i>
								<span>Quay lại</span>
							</button>
						)}
						<h2>📋 Lịch nhắc uống thuốc</h2>
					</div>
					<div className="date-selector">
						<input
							type="date"
							value={selectedDate}
							onChange={(e) => setSelectedDate(e.target.value)}
							className="date-input"
						/>
						<span className="date-display">{todayFormatted}</span>
					</div>
				</div>

				{actionError && <p className="error-state" style={{ marginBottom: 12 }}>{actionError}</p>}

				{checklist.length === 0 ? (
					<div className="empty-state">
						<div className="empty-icon">🎉</div>
						<p>Tuyệt vời! Hôm nay cụ không có lịch phải uống thuốc nào.</p>
					</div>
				) : (
					<div className="task-groups">
						{groupedTasks.map(([time, tasks]) => (
							<div key={time} className="task-group">
								<div className="group-header">
									<span className="group-time">⏰ {formatTime(time)}</span>
									<span className="group-count">{tasks.length} thuốc</span>
								</div>
								<div className="tasks-in-time">
									{tasks.map((task) => {
										const isTaken = task.status === 'taken';
										const canConfirm = canConfirmMedicationTaken(task);
										return (
											<div key={task.logId} className={`task-card task-${task.status}`}>
												<div className="task-info">
													<h3 className="med-name">{task.medicationName}</h3>
													<p className="med-dosage">💊 {task.dosage}</p>
													<p className="med-remind-time">⏰ Uống lúc {formatTime(task.remindTime)}</p>
													{isTaken && task.takenAt && (
														<p className="taken-time">✅ Đã uống lúc {formatDateTime(task.takenAt)}</p>
													)}
												</div>
												<div className="task-action">
													{isTaken ? (
														<span className="btn-action btn-taken" style={{ cursor: 'default' }}>✅ ĐÃ UỐNG</span>
													) : canConfirm ? (
														<button
															className="btn-action btn-pending"
															style={{ width: '100%' }}
															onClick={() => {
																setActionError('');
																setExpandedTask(expandedTask === task.logId ? null : task.logId);
															}}
														>
															✅ Đã uống
														</button>
													) : null}
												</div>

												{expandedTask === task.logId && canConfirm && !isTaken && (
													<div className="side-effect-input">
														<label>Ghi chú tác dụng phụ (tùy chọn):</label>
														<textarea
															placeholder="VD: Buồn nôn, chóng mặt..."
															value={sideEffectNote[task.logId] || ''}
															onChange={(e) =>
																setSideEffectNote((prev) => ({
																	...prev,
																	[task.logId]: e.target.value,
																}))
															}
														/>
														<button
															className="btn-action btn-pending"
															style={{ width: '100%', marginTop: 10 }}
															onClick={() => handleConfirmTaken(task)}
														>
															Xác nhận đã uống
														</button>
													</div>
												)}
											</div>
										);
									})}
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default MedicationChecklist;
